// Wang tileset renderer. Consumes the PixelLab topdown tileset metadata
// (a list of 16 tiles, each keyed by the four-corner terrain pattern)
// and renders a terrain grid onto the scene.
//
// Terrain grid: (cols+1) x (rows+1) vertices. Each vertex is "upper" (wall)
// or "lower" (floor). Each tile cell looks at its four corner vertices
// (NW, NE, SW, SE) to pick which Wang tile to paint.

// Build a lookup from corner pattern (string like "upper|upper|lower|lower")
// to the tile's bounding box in the tileset PNG.
export function buildCornerMap(metadata) {
  // PixelLab nests tiles under tileset_data.tiles; some older formats use
  // metadata.tiles directly. Accept either.
  const tiles = metadata?.tileset_data?.tiles ?? metadata?.tiles ?? [];
  const map = new Map();
  for (const tile of tiles) {
    const key = cornerKey(tile.corners.NW, tile.corners.NE, tile.corners.SW, tile.corners.SE);
    map.set(key, tile.bounding_box);
  }
  return map;
}

function cornerKey(nw, ne, sw, se) {
  return `${nw}|${ne}|${sw}|${se}`;
}

// Register the Wang tiles as named frames on a Phaser texture so we can
// render them by key. Call this during preload/create after the tileset
// image has loaded.
export function registerWangFrames(scene, textureKey, metadata) {
  const texture = scene.textures.get(textureKey);
  const cornerMap = buildCornerMap(metadata);
  let i = 0;
  for (const [key, box] of cornerMap.entries()) {
    const frameName = `wang_${i++}`;
    texture.add(frameName, 0, box.x, box.y, box.width, box.height);
    // Remember the frame name on the map for lookup
    cornerMap.set(key, frameName);
  }
  return cornerMap;
}

// Paint an N-col by M-row terrain grid with Wang tiles.
// Pass a terrain(vx, vy) -> "upper"|"lower" function describing the vertex grid.
export function paintTerrain(scene, textureKey, cornerMap, cols, rows, tileSize, terrain) {
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      const nw = terrain(cx,   cy);
      const ne = terrain(cx+1, cy);
      const sw = terrain(cx,   cy+1);
      const se = terrain(cx+1, cy+1);
      const key = cornerKey(nw, ne, sw, se);
      const frame = cornerMap.get(key);
      if (!frame) continue; // unmatched corner pattern -- skip
      const x = cx * tileSize + tileSize / 2;
      const y = cy * tileSize + tileSize / 2;
      const img = scene.add.image(x, y, textureKey, frame);
      img.setDisplaySize(tileSize, tileSize);
      img.setDepth(0);
    }
  }
}
